import { Controller } from '@hotwired/stimulus'
import { post } from '@rails/request.js'
import { humanDuration } from '../utils'
import { enableWakeLock, disableWakeLock } from '../utils/wake_lock'

// Connects to data-controller="player"
export default class extends Controller {
  static targets = ['currentTrackTitle',
                    'currentAlbumTitle',
                    'currentAlbumJacket',
                    'currentAlbumLink',
                    'currentPlayingTime',
                    'remainingPlayTime',
                    'seekBar',
                    'playOrPauseBtn',
                    'nextBtn',
                    'prevBtn',
                    'repeatBtn',
                    'playSpeedBtn',
                    'changePlaySpeedBtn',
                    'currentPlaySpeed',
                    'playSpeedPopover']

  static values = { tracks: Object,
                    trackIds: Array,
                    currentTrackId: Number,
                    isPlaying: Boolean,
                    repeat: String,
                    playSpeed: Number,
                    isOpenPlaySpeedPopover: Boolean,
                    repeatOptions: Array,
                    playSpeedOptions: Array,
                    playHistoryUrl: String }

  static outlets = ['body', 'track', 'tab-unique-id']

  initialized = false
  currentTrack = null
  realtimeSyncPlayingTimeIntervalRequest = null
  playedTime = 0
  isCreatedPlayHistory = false
  playAttemptId = 0

  connect() {
    if (this.initialized) {
      this.syncTrack({ scroll: true })
      this.collapse()
    }
    this.initialized = true
  }

  disconnect() {
    this.collapse()
  }

  initialize() {
    this.expand() // ページ遷移するまでは広げる

    window.audio.pause()
    window.audio.onplay = () => {
      this.isPlayingValue = true
      window.audio.playbackRate = this.getCurrentPlaySpeed()
      this.syncTrack({ scroll: false })
      this.startRealtimeSyncPlayingTime()
      enableWakeLock()
    }
    window.audio.onpause = () => {
      this.syncTrack({ scroll: false })
      this.stopRealtimeSyncPlayingTime()
      disableWakeLock()
    }
    window.audio.onended = () => {
      // NOTE: onended では disableWakeLock() を呼ばない
      // next() で次のトラックが再生される場合にロックを維持するため
      this.next({ byAutoPlay: true })
    }

    this.restoreLocalStorageValues()
    this.isPlayingValue = true
  }

  restoreLocalStorageValues() {
    const repeat = localStorage.getItem('repeat')
    const playSpeed = Number(localStorage.getItem('playSpeed'))

    if (repeat) {
      this.repeatValue = repeat
    }
    if (playSpeed && !isNaN(playSpeed)) {
      this.playSpeedValue = playSpeed
    }
  }

  // ================ valueChanged

  currentTrackIdValueChanged() {
    this.currentTrack = this.tracksValue[this.currentTrackIdValue]
    this.playedTime = 0
    this.isCreatedPlayHistory = false

    window.audio.setAttribute('src', this.currentTrack.url)

    this.currentTrackTitleTarget.innerHTML = this.currentTrack.title
    this.currentAlbumTitleTarget.innerHTML = this.currentTrack.album.title
    this.currentAlbumJacketTarget.setAttribute('src', this.currentTrack.album.jacket_url)
    this.currentAlbumLinkTarget.setAttribute('href', this.currentTrack.album.url)
    this.currentPlayingTimeTarget.innerHTML = humanDuration(0)
    this.remainingPlayTimeTarget.innerHTML = `-${humanDuration(this.currentTrack.duration)}`
    this.seekBarTarget.max = this.currentTrack.duration
    this.seekBarTarget.value = 0
    this.prevBtnTarget.disabled = false
    this.nextBtnTarget.disabled = false

    this.playSpeedBtnTarget.disabled = this.currentTrack.music_mode
    this.currentPlaySpeedTarget.innerHTML = this.getCurrentPlaySpeed()

    if (this.trackIdsValue.indexOf(this.currentTrackIdValue) === 0) {
      this.prevBtnTarget.disabled = true
    } else if (this.trackIdsValue.indexOf(this.currentTrackIdValue) === this.trackIdsValue.length - 1) {
      this.nextBtnTarget.disabled = true
    }

    this.syncTrack({ scroll: true })
    if (this.isPlayingValue) {
      this.start()
    }
  }

  syncTrack({ scroll }) {
    if (this.hasTrackOutlet) {
      this.trackOutlets.forEach((trackOutlet) => {
        if (trackOutlet.trackIdValue === this.currentTrackIdValue) {
          if (scroll) trackOutlet.scrollToPlayingTrack()
          trackOutlet.trackTarget.dataset.status = this.isPlayingValue ? 'playing' : 'paused'
        } else {
          trackOutlet.trackTarget.dataset.status = 'stopped'
        }
      })
    }
  }

  isPlayingValueChanged() {
    this.playOrPauseBtnTarget.dataset.status = this.isPlayingValue ? 'paused' : 'playing'
  }

  repeatValueChanged() {
    localStorage.setItem('repeat', this.repeatValue)
    this.repeatBtnTarget.dataset.repeat = this.repeatValue
  }

  playSpeedValueChanged() {
    localStorage.setItem('playSpeed', this.playSpeedValue)

    this.changePlaySpeedBtnTargets.forEach((changePlaySpeedBtn) => {
      changePlaySpeedBtn.dataset.current = changePlaySpeedBtn.dataset.playerPlaySpeedParam === this.playSpeedValue.toString()
    })
    this.currentPlaySpeedTarget.innerHTML = this.getCurrentPlaySpeed()

    if (window.audio) {
      window.audio.playbackRate = this.getCurrentPlaySpeed()
    }
  }

  getCurrentPlaySpeed() {
    return this.currentTrack.music_mode ? 1 : this.playSpeedValue
  }

  // ================ by Audio Events

  startRealtimeSyncPlayingTime = () => {
    const fps = 60
    this.realtimeSyncPlayingTimeIntervalRequest = setInterval(() => {
      this.syncPlayingTime()
    }, 1000 / fps)
  }

  stopRealtimeSyncPlayingTime() {
    clearInterval(this.realtimeSyncPlayingTimeIntervalRequest)
  }

  syncPlayingTime() {
    const nextCurrentPlayingTimeText = humanDuration(window.audio.currentTime)
    const nextRemainingPlayTimeTargetText = `-${humanDuration(this.currentTrack.duration - window.audio.currentTime)}`

    this.refreshPlayingTime({ nextCurrentPlayingTimeText, nextRemainingPlayTimeTargetText })
  }

  // ================ Actions

  start() {
    // 新しい再生試行を開始するため、古いリトライチェーンを無効化する
    const currentAttemptId = ++this.playAttemptId

    // NOTE: 最新のiOSだとなぜかcanplayイベントが発火されないのでコメントアウト
    // window.audio.oncanplay = () => {
    //   window.audio.play()
    //   window.audio.oncanplay = null
    // }

    // URLの有効期限切れを想定して、ネットワークエラーの場合のみリトライする
    // ただし単純なネットワークエラーの場合も考えられるため、リトライ回数を制限する
    const initialRetryCount = 10
    let retryCount = initialRetryCount
    window.audio.onerror = () => {
      if (window.audio.error.code === MediaError.MEDIA_ERR_NETWORK && retryCount-- > 0) {
        const currentTime = window.audio.currentTime
        window.audio.oncanplay = () => {
          window.audio.currentTime = currentTime
          if (this.isPlayingValue) {
            this.playWithRetry(currentAttemptId)
          }
          window.audio.oncanplay = null
          retryCount = initialRetryCount
        }
        window.audio.load()
      }
    }

    window.audio.load()

    // NOTE: 最新のiOSだとなぜかcanplayイベントが発火されないので手動実行
    this.playWithRetry(currentAttemptId)
  }

  playWithRetry(attemptId, maxRetries = 10, attempt = 1) {
    // 別の再生試行が開始されている場合はこのチェーンを中止する
    if (attemptId !== this.playAttemptId) return

    window.audio.play().catch((e) => {
      // load() の再呼び出し等で play() が中断された場合は無視する
      if (e.name === 'AbortError') return

      console.log(`PLAY ERROR (attempt ${attempt}/${maxRetries}):`, e)

      if (attempt < maxRetries && this.isPlayingValue) {
        setTimeout(() => {
          if (this.isPlayingValue && attemptId === this.playAttemptId) {
            this.playWithRetry(attemptId, maxRetries, attempt + 1)
          }
        }, 1000)
      } else {
        // リトライ上限に達したので再生を停止する
        this.isPlayingValue = false
        this.syncTrack({ scroll: false })
        disableWakeLock()
      }
    })
  }

  playOrPause() {
    if (this.isPlayingValue) {
      window.audio.pause()
      this.isPlayingValue = false
    } else {
      const currentAttemptId = ++this.playAttemptId
      this.playWithRetry(currentAttemptId)
    }
  }

  next({ byAutoPlay = false }) {
    if (byAutoPlay && this.repeatValue === 'one') {
      this.start()
      this.playedTime = 0
      this.isCreatedPlayHistory = false
    } else {
      const nextTrack = this.incrementTrackNumber()
      if (!nextTrack) {
        this.finish()
      }
      // NOTE: incrementTrackNumber() による currentTrackIdValue の変更で
      // currentTrackIdValueChanged() → start() が既に呼ばれるため、ここでは呼ばない
    }
  }

  prev() {
    const prevTrack = this.decrementTrackNumber()
    if (!prevTrack) {
      this.finish()
      window.audio.currentTime = 0
      this.refreshPlayingTime({
        nextCurrentPlayingTimeText: humanDuration(0),
        nextRemainingPlayTimeTargetText: `-${humanDuration(this.currentTrack.duration)}`
      })
    }
    // NOTE: decrementTrackNumber() による currentTrackIdValue の変更で
    // currentTrackIdValueChanged() → start() が既に呼ばれるため、ここでは呼ばない
  }

  finish() {
    window.audio.pause()
    this.isPlayingValue = false
    disableWakeLock()
    this.refreshPlayingTime({
      nextCurrentPlayingTimeText: humanDuration(this.currentTrack.duration),
      nextRemainingPlayTimeTargetText: `-${humanDuration(0)}`
    })
  }

  seek() {
    window.audio.currentTime = this.seekBarTarget.value
    this.syncPlayingTime()
  }

  changeRepeat = () => {
    const currentIndex = this.repeatOptionsValue.indexOf(this.repeatValue)
    if (currentIndex < this.repeatOptionsValue.length - 1) {
      this.repeatValue = this.repeatOptionsValue[currentIndex + 1]
    } else {
      this.repeatValue = this.repeatOptionsValue[0]
    }
  }

  togglePlaySpeedOptions() {
    if (this.isOpenPlaySpeedPopoverValue) {
      this.closePlaySpeedOptions()
    } else {
      this.openPlaySpeedOptions()
    }
  }

  openPlaySpeedOptions() {
    this.isOpenPlaySpeedPopoverValue = true
    this.playSpeedPopoverTarget.dataset.active = true
    this.closePlaySpeedOptionsListener = this.closePlaySpeedOptionsListener.bind(this)
    document.addEventListener('click', this.closePlaySpeedOptionsListener, true)
  }

  closePlaySpeedOptionsListener(e) {
    if (!e.target.closest('[data-player-target="playSpeedPopover"]') && !e.target.closest('[data-player-target="playSpeedBtn"]')) {
      this.closePlaySpeedOptions()
    }
  }

  closePlaySpeedOptions() {
    this.isOpenPlaySpeedPopoverValue = false
    this.playSpeedPopoverTarget.dataset.active = false
    document.removeEventListener('click', this.closePlaySpeedOptionsListener, true)
  }

  changePlaySpeed({ params: { playSpeed } }) {
    this.playSpeedValue = playSpeed
    this.closePlaySpeedOptions()
  }

  collapse() {
    this.bodyOutlet.playerTarget.dataset.status = 'collapsed'
    this.bodyOutlet.frameTarget.dataset.hasPlayer = 'collapsed'
  }

  expand() {
    this.bodyOutlet.playerTarget.dataset.status = 'expanded'
    this.bodyOutlet.frameTarget.dataset.hasPlayer = 'expanded'
  }

  // ======== Utilities

  incrementTrackNumber() {
    const currentIndex = this.trackIdsValue.indexOf(this.currentTrackIdValue)
    if (currentIndex < this.trackIdsValue.length - 1) {
      return this.currentTrackIdValue = this.trackIdsValue[currentIndex + 1]
    } else if (this.repeatValue === 'all') {
      return this.currentTrackIdValue = this.trackIdsValue[0]
    } else {
      return false
    }
  }

  decrementTrackNumber() {
    const currentIndex = this.trackIdsValue.indexOf(this.currentTrackIdValue)
    if (currentIndex > 0) {
      return this.currentTrackIdValue = this.trackIdsValue[currentIndex - 1]
    } else {
      return false
    }
  }

  refreshPlayingTime({ nextCurrentPlayingTimeText, nextRemainingPlayTimeTargetText }) {
    const currentPlayingTimeText = this.currentPlayingTimeTarget.innerHTML
    const remainingPlayingTimeText = this.remainingPlayTimeTarget.innerHTML

    // 更新不可軽減のため秒数が変化しない場合は処理をスキップ
    if (currentPlayingTimeText !== nextCurrentPlayingTimeText) {
      this.currentPlayingTimeTarget.innerHTML = nextCurrentPlayingTimeText
      if (this.isPlayingValue) {
        this.playedTime += 1
        this.createPlayHistory()
      }
    }
    if (remainingPlayingTimeText !== nextRemainingPlayTimeTargetText) {
      this.remainingPlayTimeTarget.innerHTML = nextRemainingPlayTimeTargetText
    }

    this.seekBarTarget.value = window.audio.currentTime
  }

  createPlayHistory() {
    const threshold = this.currentTrack.duration / 2

    if (!this.isCreatedPlayHistory && this.playedTime > threshold) {
      this.isCreatedPlayHistory = true

      // NOTE: 再生履歴の記録の成否は確認しない
      post(this.playHistoryUrlValue, {
        responseKind: 'json',
        body: {
          track_id: this.currentTrackIdValue, // eslint-disable-line camelcase
          play_speed: this.playSpeedValue, // eslint-disable-line camelcase
          tab_unique_id: this.tabUniqueIdOutlet.tabUniqueIdValue // eslint-disable-line camelcase
        }
      })

      // 未読バッジを削除
      const targetTrack = this.trackOutlets.find((trackOutlet) => (
        trackOutlet.trackIdValue === this.currentTrackIdValue
      ))
      if (targetTrack) {
        targetTrack.markAsPlayed()
      }
    }
  }
}
